import { ENV } from '@/config/env/env.read';

type TypePushSupport = { supported: boolean; ios: boolean; android: boolean; standalone: boolean; needsInstall: boolean; secure: boolean };

const ACTIVE_KEY = 'bd-push-active';
const OPT_OUT_KEY = 'bd-push-optout';
const SNOOZE_KEY = 'bd-push-snooze-until';
const SNOOZE_MS = 14 * 86_400_000;

export const pushSupport = (): TypePushSupport => {
    if (typeof window === 'undefined') return { supported: false, ios: false, android: false, standalone: false, needsInstall: false, secure: false };
    const agent = navigator.userAgent;
    const ios = /iPhone|iPad|iPod/.test(agent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    const android = /Android/.test(agent);
    const standalone = window.matchMedia?.('(display-mode: standalone)').matches || (navigator as unknown as { standalone?: boolean }).standalone === true;
    const supported = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window && window.isSecureContext;
    return { supported, ios, android, standalone, needsInstall: ios && !standalone, secure: window.isSecureContext };
};

export const pushDeviceLabel = (): { label: string; platform: string } => {
    const agent = navigator.userAgent;
    const browser = /SamsungBrowser/.test(agent) ? 'Samsung Internet' : /Edg\//.test(agent) ? 'Edge' : /Firefox\//.test(agent) ? 'Firefox' : /CriOS|Chrome\//.test(agent) ? 'Chrome' : /Safari\//.test(agent) ? 'Safari' : 'Browser';
    if (/iPhone/.test(agent)) return { label: 'iPhone', platform: 'ios' };
    if (/iPad/.test(agent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) return { label: 'iPad', platform: 'ios' };
    if (/Android/.test(agent)) return { label: /SM-|Samsung/.test(agent) ? `Samsung · ${browser}` : `Android · ${browser}`, platform: 'android' };
    if (/Mac OS X/.test(agent)) return { label: `Mac · ${browser}`, platform: 'mac' };
    if (/Windows/.test(agent)) return { label: `Windows · ${browser}`, platform: 'windows' };
    return { label: browser, platform: 'other' };
};

export const pushWorkerUrl = (apiUrl: string): string => (apiUrl ? `/sw.js?api=${encodeURIComponent(apiUrl)}` : '/sw.js');

export const pushRegistration = async (): Promise<ServiceWorkerRegistration | null> => {
    if (!pushSupport().supported) return null;
    try {
        return await navigator.serviceWorker.register(pushWorkerUrl(ENV.apiUrl), { scope: '/' });
    } catch {
        return null;
    }
};

export const pushCurrent = async (): Promise<PushSubscription | null> => {
    const registration = await pushRegistration();
    if (!registration) return null;
    try {
        return await registration.pushManager.getSubscription();
    } catch {
        return null;
    }
};

const keyBytes = (base64url: string): Uint8Array => {
    const padded = `${base64url}${'='.repeat((4 - (base64url.length % 4)) % 4)}`.replace(/-/g, '+').replace(/_/g, '/');
    const raw = window.atob(padded);
    return Uint8Array.from(raw, (char) => char.charCodeAt(0));
};

export const pushSubscribe = async (publicKey: string): Promise<PushSubscription | 'denied' | null> => {
    const registration = await pushRegistration();
    if (!registration) return null;
    const permission = Notification.permission === 'granted' ? 'granted' : await Notification.requestPermission();
    if (permission !== 'granted') return 'denied';
    const existing = await registration.pushManager.getSubscription();
    if (existing) return existing;
    return registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(publicKey) as BufferSource });
};

export const pushUnsubscribe = async (): Promise<void> => {
    const current = await pushCurrent();
    if (current) await current.unsubscribe().catch(() => false);
    pushMarkActive(false);
};

export const pushFingerprint = async (endpoint: string): Promise<string> => {
    const digest = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(endpoint));
    return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('').slice(0, 16);
};

export const pushMarkActive = (active: boolean): void => {
    try {
        if (active) window.localStorage.setItem(ACTIVE_KEY, '1');
        else window.localStorage.removeItem(ACTIVE_KEY);
    } catch {
        return;
    }
};

export const pushIsActive = (): boolean => {
    try {
        return window.localStorage.getItem(ACTIVE_KEY) === '1';
    } catch {
        return false;
    }
};

export const pushKeys = (subscription: PushSubscription): { endpoint: string; p256dh: string; auth: string } | null => {
    const json = subscription.toJSON();
    if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) return null;
    return { endpoint: json.endpoint, p256dh: json.keys.p256dh, auth: json.keys.auth };
};

const readKey = (key: string): string | null => {
    try {
        return window.localStorage.getItem(key);
    } catch {
        return null;
    }
};

const writeKey = (key: string, value: string | null): void => {
    try {
        if (value === null) window.localStorage.removeItem(key);
        else window.localStorage.setItem(key, value);
    } catch {
        return;
    }
};

export const pushOptedOut = (): boolean => readKey(OPT_OUT_KEY) === '1';

export const pushSetOptOut = (value: boolean): void => writeKey(OPT_OUT_KEY, value ? '1' : null);

export const pushSnoozed = (now: number = Date.now()): boolean => Number(readKey(SNOOZE_KEY) ?? 0) > now;

export const pushSnooze = (now: number = Date.now()): void => writeKey(SNOOZE_KEY, String(now + SNOOZE_MS));

export const pushUnblockHelp = (): string => {
    const agent = navigator.userAgent;
    const ios = /iPhone|iPad|iPod/.test(agent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    if (ios) return 'Impostazioni dell’iPhone → Notifiche → beDashboard → Consenti notifiche.';
    if (/SamsungBrowser/.test(agent)) return 'Menu ≡ → Impostazioni → Siti e download → Notifiche → consenti questo sito, poi ricarica.';
    if (/Android/.test(agent)) return 'Tocca l’icona a sinistra dell’indirizzo → Autorizzazioni → Notifiche → Consenti, poi ricarica.';
    if (/Firefox\//.test(agent)) return 'Clicca l’icona a sinistra dell’indirizzo → togli il blocco alle Notifiche, poi ricarica.';
    if (/Safari\//.test(agent) && !/Chrome|Chromium|Edg\//.test(agent)) return 'Safari → Impostazioni → Siti web → Notifiche → questo sito: Consenti, poi ricarica.';
    return 'Clicca l’icona a sinistra dell’indirizzo → Impostazioni sito → Notifiche: Consenti, poi ricarica la pagina.';
};
